import clsx from 'clsx';
import * as Headless from '@headlessui/react';
import {
  ClipboardDocumentCheckIcon,
  EllipsisHorizontalIcon,
  PencilSquareIcon,
  SparklesIcon,
  TrashIcon
} from '@heroicons/react/24/outline';
import { StopIcon } from '@heroicons/react/20/solid';
import { Spinner } from '@oysterjs/ui/spinner';

/**
 * Shared menu-panel class — mirrors ATTACHMENT_PICKER_MENU_CLASS from
 * cara-input-attachment-button.tsx so both menus look identical.
 */
const MENU_PANEL_CLASS = clsx(
  'absolute right-0 z-20 mt-1 w-44 origin-top-right rounded-md bg-white p-1 shadow-lg ring-1 ring-black/5',
  'focus:outline-none',
  'transition data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0',
  'data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in',
  'dark:bg-neutral-900 dark:ring-white/10'
);

const MENU_ITEM_CLASS = clsx(
  'group flex w-full items-center gap-2.5 rounded px-3 py-2 text-left text-sm',
  'text-neutral-700 dark:text-neutral-200',
  'data-[focus]:bg-neutral-100 data-[focus]:text-neutral-900',
  'dark:data-[focus]:bg-neutral-800 dark:data-[focus]:text-white',
  'data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50'
);

const MENU_ITEM_ICON_CLASS = clsx(
  'size-4 shrink-0',
  'text-neutral-500 dark:text-neutral-400',
  'group-data-[focus]:text-neutral-700 dark:group-data-[focus]:text-neutral-200'
);

const DESTRUCTIVE_MENU_ITEM_CLASS = clsx(
  'group flex w-full items-center gap-2.5 rounded px-3 py-2 text-left text-sm',
  'text-rose-600 dark:text-rose-400',
  'data-[focus]:bg-rose-50 dark:data-[focus]:bg-rose-500/10 data-[focus]:text-rose-700 dark:data-[focus]:text-rose-300'
);

const DESTRUCTIVE_MENU_ITEM_ICON_CLASS = clsx(
  'size-4 shrink-0',
  'text-rose-500 dark:text-rose-400',
  'group-data-[focus]:text-rose-600 dark:group-data-[focus]:text-rose-400'
);

/**
 * Right-side action cluster of the workspace top bar.
 *
 * - Stop button is rendered only when the workspace has work to interrupt
 *   (i.e. `canStop`). Mounting it conditionally keeps the bar quiet whenever
 *   the workspace is idle.
 */
export const WorkspaceTopBarActions = (props: {
  /** False when the caller may read this workspace but not change it. */
  canUpdate: boolean;
  canStop: boolean;
  onStop: () => void;
  stopLoading: boolean;
  onRename: () => void;
  renameDisabled: boolean;
  onArchive: () => void;
  onConvertToPreset?: () => void;
  onOpenSmartFillBucket?: () => void;
}) => (
  <div className="flex flex-row items-center gap-1.5 shrink-0">
    {props.canStop && (
      <Headless.Button
        type="button"
        onClick={props.onStop}
        disabled={props.stopLoading}
        aria-label="Stop workspace"
        className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium text-rose-700 dark:text-rose-300 bg-rose-50 dark:bg-rose-500/10 ring-1 ring-rose-100 dark:ring-rose-500/20 hover:bg-rose-100 dark:hover:bg-rose-500/20 transition-colors duration-150 data-[disabled]:opacity-60 data-[disabled]:cursor-default"
      >
        {props.stopLoading ? (
          <Spinner size="size-3.5" color="rose" />
        ) : (
          <StopIcon className="size-3.5" />
        )}
        Stop
      </Headless.Button>
    )}
    {(props.canUpdate || props.onConvertToPreset || props.onOpenSmartFillBucket) && (
      <OverflowMenu
        canUpdate={props.canUpdate}
        onRename={props.onRename}
        renameDisabled={props.renameDisabled}
        onArchive={props.onArchive}
        onConvertToPreset={props.onConvertToPreset}
        onOpenSmartFillBucket={props.onOpenSmartFillBucket}
      />
    )}
  </div>
);

const OverflowMenu = (props: {
  canUpdate: boolean;
  onRename: () => void;
  renameDisabled: boolean;
  onArchive: () => void;
  onConvertToPreset?: () => void;
  onOpenSmartFillBucket?: () => void;
}) => (
  <Headless.Menu as="div" className="relative">
    <Headless.MenuButton
      aria-label="Workspace actions"
      className="inline-flex items-center justify-center size-8 rounded-full text-neutral-500 dark:text-neutral-400 hover:text-neutral-900 dark:hover:text-white hover:bg-neutral-100 dark:hover:bg-white/10 transition-colors duration-150"
    >
      <EllipsisHorizontalIcon className="size-5" aria-hidden="true" />
    </Headless.MenuButton>
    <Headless.MenuItems transition className={MENU_PANEL_CLASS}>
      {props.canUpdate && (
        <Headless.MenuItem disabled={props.renameDisabled}>
          <Headless.Button
            type="button"
            onClick={props.onRename}
            disabled={props.renameDisabled}
            className={MENU_ITEM_CLASS}
          >
            <PencilSquareIcon className={MENU_ITEM_ICON_CLASS} aria-hidden="true" />
            Rename
          </Headless.Button>
        </Headless.MenuItem>
      )}
      {props.onConvertToPreset && (
        <Headless.MenuItem>
          <Headless.Button
            type="button"
            onClick={props.onConvertToPreset}
            className={MENU_ITEM_CLASS}
          >
            <SparklesIcon className={MENU_ITEM_ICON_CLASS} aria-hidden="true" />
            Convert to Preset
          </Headless.Button>
        </Headless.MenuItem>
      )}
      {props.onOpenSmartFillBucket && (
        <Headless.MenuItem>
          <Headless.Button
            type="button"
            onClick={props.onOpenSmartFillBucket}
            className={MENU_ITEM_CLASS}
          >
            <ClipboardDocumentCheckIcon className={MENU_ITEM_ICON_CLASS} aria-hidden="true" />
            Smart Fill bucket
          </Headless.Button>
        </Headless.MenuItem>
      )}
      {props.canUpdate && (
        <Headless.MenuItem>
          <Headless.Button
            type="button"
            onClick={props.onArchive}
            className={DESTRUCTIVE_MENU_ITEM_CLASS}
          >
            <TrashIcon className={DESTRUCTIVE_MENU_ITEM_ICON_CLASS} aria-hidden="true" />
            Archive
          </Headless.Button>
        </Headless.MenuItem>
      )}
    </Headless.MenuItems>
  </Headless.Menu>
);
